<script>
   /*************************************************************/
   /* ModelLoader                                               */
   /* -------------------------                                 */
   /* loads PLS1DA model from file                                 */
   /*************************************************************/

   import { FileSelector, ButtonCancel, Container, Button } from 'svelte-controls-basic';
   import { reviver, modelsFit } from '@mdatools-apps/shared/utils';
   import { checkPrepField } from '@mdatools-apps/shared/methods/prep.js';
   import PrepInfo from '@mdatools-apps/shared/components/PrepInfo.svelte';

   let { model = $bindable() } = $props();    // object with model


   // string with model file name
   let modelFile = $state('');

   // app status for handling and showing errors
   let status = $state('neutral');
   let errMsg = $state('');

   // gets model content as one large string, parse it into matrix
   function setModel(strData) {
      try{
         const m = JSON.parse(strData, reviver);

         // every step here has to hold before the next one is allowed to look inside: reaching
         // 'm.pls.v' while 'pls' was missing (a pls model file keeps its fields at the top level)
         // raised a raw TypeError, and the user was shown its text instead of a sentence about
         // the file. 'da' has to be an object, not merely a key: with '"da": null' the info panel
         // below threw on 'model.da.ncomp' during render, where no catch reaches
         const isObject = v => v !== null && typeof v === 'object';
         const isList = v => Array.isArray(v) || ArrayBuffer.isView(v);
         if (!isObject(m) || !isObject(m.pls) || !Array.isArray(m.pls.class) ||
             m.pls.class[0] !== 'plsmodel' || m.pls.nresp !== 1 || !isObject(m.da)) {
            throw Error('The JSON file you selected does not contain PLS1-DA model.');
         }

         if (!Number.isInteger(m.pls.ncomp) || m.pls.ncomp < 1) {
            throw Error('The model file is damaged: it has no valid number of LVs.');
         }

         // read where no catch reaches: 'exclvars' by the info panel, 'varlabels' and 'varvalues'
         // by the contributions plot, 'nrows' by the distance plot, where a missing value hands
         // NaN to 'qchisq' and the outlier limit is silently wrong. containers only: the
         // prediction code refuses a bad 'exclvars' entry, a bad label or value only draws wrong
         const broken = [
            ['exclvars', Array.isArray(m.pls.exclvars)],
            ['varlabels', Array.isArray(m.pls.varlabels)],
            ['varvalues', Array.isArray(m.pls.varvalues)],
            ['nrows', Number.isInteger(m.pls.nrows) && m.pls.nrows > 0],
         ].find(([, ok]) => !ok);
         if (broken) {
            throw Error("The model file is damaged: field '" + broken[0] + "' is missing or broken.");
         }

         // the preprocessing is rendered below, where no catch reaches, and applied by
         // 'app_pls_predict': the checks of a preprocessing model, none for 0 or an empty object
         try { checkPrepField(m.pls.prep); } catch {
            throw Error('The model file is damaged: its preprocessing can not be read.');
         }

         // the classifier: every field below is read where no catch reaches - the settings
         // widget, 'predict' and the classification helpers, the discrimination and ROC plots,
         // the results table, the exporter. one sentence covers the shape of it all; the two
         // values a user can act on get their own
         const da = m.da;
         const daMsg = 'The model file is damaged: its classification part can not be read.';

         // 'predict' keys the results by class name, so a duplicated name overwrites one class
         const cn = da.classNames;
         if (!Array.isArray(cn) || cn.length !== 2 || !cn.every(v => typeof v === 'string') ||
             cn[0] === cn[1]) {
            throw Error(daMsg);
         }

         // 'predict' keeps the totals under 'total', next to the classes (see 'DataLoader')
         if (cn.includes('total')) {
            throw Error("The model file names a class 'total', which the results keep for " +
               'their totals.');
         }

         // the selected number of LVs indexes the results on the first render of the results
         // table, before the slider's clamp could rewrite it, and the predict tab has no slider
         if (!Number.isInteger(da.ncomp) || da.ncomp < 1) {
            throw Error('The model file is damaged: the number of LVs of the classifier is not ' +
               'a whole number above zero.');
         }
         if (da.ncomp > m.pls.ncomp) {
            throw Error('The model file is damaged: it selects ' + da.ncomp + ' LVs while the ' +
               'model has only ' + m.pls.ncomp + '.');
         }

         // a rule outside the two silently classifies with the soft one; a limit type outside
         // the two indexes 'params' with it
         if (!['hard', 'soft'].includes(da.rule) || !['classic', 'robust'].includes(da.limType)) {
            throw Error(daMsg);
         }

         // the controls write numbers, but a decimal string is honest too (an older control could
         // have stored the slider's string), and the code converts the three settings two ways:
         // alpha with an implicit 'Number()' in 'classifySoft' and with 'Number.parseFloat()' in
         // the settings widget and the heatmap, gamma with 'parseFloat()' only, threshold with
         // 'Number()' only. a value is accepted when both conversions give the same finite
         // number, which refuses '0x10' (16 against 0), '', null and true. alpha and gamma are
         // bounded because 'qnorm' throws for a probability outside [0, 1] and the settings
         // widget calls it in an effect
         const isNum = v => (typeof v === 'number' || typeof v === 'string') &&
            Number.isFinite(Number(v)) && Number(v) === Number.parseFloat(v);
         const inRange = (v, lo, hi) => isNum(v) && Number(v) >= lo && Number(v) <= hi;
         if (!inRange(da.alpha, 0, 100) || !inRange(da.gamma, 0, 100) || !isNum(da.threshold)) {
            throw Error(daMsg);
         }

         // the two member counts feed '1 - (1 - gamma)^(1/dof)', the outlier probability, which
         // 'qnorm' refuses below zero; the class parameters feed 'pnorm' and 'qnorm', where a
         // null deviation (the NaN of a one-member class, stored as null) turns the soft rule
         // into a threshold at the class mean with the critical limit drawn on it, and an entry
         // that does not convert to a number gives NaN, which rejects every object; both
         // silently. checked for every LV and both limit types, because the test tab computes
         // its statistics over all of them
         const limMsg = 'The model file is damaged: the classification limits of a class can ' +
            'not be used.';
         if (!Array.isArray(da.dof) || da.dof.length !== 2 ||
             !da.dof.every(v => Number.isInteger(v) && v >= 1)) {
            throw Error(limMsg);
         }
         if (!isObject(da.params)) throw Error(daMsg);
         for (const c of cn) {
            if (!isObject(da.params[c])) throw Error(daMsg);
            for (const t of ['classic', 'robust']) {
               const p = da.params[c][t];
               if (!isObject(p) || !isList(p.m) || !isList(p.s) ||
                   p.m.length < m.pls.ncomp || p.s.length < m.pls.ncomp) {
                  throw Error(daMsg);
               }
               if (!Array.from(p.m).every(Number.isFinite) ||
                   !Array.from(p.s).every(Number.isFinite)) {
                  throw Error(limMsg);
               }
            }
         }

         // the critical limits are drawn and printed under the soft rule: mean, deviation,
         // critical value and outlier limit per class. the settings widget writes them under
         // soft and leaves them alone under hard, so a hard-rule file holds null or a complete
         // leftover of a soft session, and nothing else: on the test tab a flip to soft reads
         // them in the same flush in which the widget rebuilds them, and the predict tab never
         // rebuilds them at all
         const crit = da.crit;
         const critOk = crit == null ? da.rule === 'hard' :
            isObject(crit) && cn.every(c => isList(crit[c]) && crit[c].length >= 4);
         if (!critOk) throw Error(daMsg);

         if (Array.isArray(m.pls.v)) {
            m.pls.v = Float64Array.from(m.pls.v);
         }
         // the model is read by offset from its sizes and kept in the session, which a model
         // whose values do not fit them would cost on its next restore
         if (!modelsFit(m)) throw Error('The model file is damaged: its values do not fit its sizes.');
         m.pls['fileName'] = modelFileName;
         model = m;
         // 'neutral', which used to be 'success': nothing told the two apart
         status = 'neutral';
         errMsg = ''
      } catch (e) {
         console.error(e);
         model = null;
         status = 'error';
         // the message only: the box printed the Error object, which reads 'Error: ...'. a file
         // the parser refuses is named as such, in place of the parser's own text
         errMsg = e instanceof SyntaxError ? 'The file could not be read as JSON.'
            : e instanceof Error ? e.message : String(e);
      }
   }

   // promise for reading the JSON file
   function readFile () {
      const reader = new FileReader();
      reader.onload = () => { setModel(reader.result) };

      // without this a file which can not be read at all - moved or made unreadable between being
      // picked and the button being pressed - left the component in its neutral state, so the
      // button appeared to do nothing
      reader.onerror = () => {
         model = null;
         status = 'error';
         errMsg = 'Could not read the file: ' +
            (reader.error ? reader.error.message : 'unknown error');
      };

      reader.readAsText(modelFile);
   }

   function reset() {
      modelFile = '';
      model = null;
      status = 'neutral';
      errMsg = '';
   }

   // reactive expression for tracking changes of 'modelFile'
   $effect(() => {
      if (modelFile === null) {
         // if model file is empty reset status and error messages
         model = null;
         status = 'neutral';
         errMsg = '';
      } else {
         // if not just remove error messages if any
         status = 'neutral';
         errMsg = '';
      }
   });

   // file name with model without extension
   let modelFileName = $derived(modelFile ? modelFile.name.replace(/\.[^/.]+$/, "") : '');
</script>

{#if !model }

   <!-- show if data is not loaded -->
   <Container name="file-selector">
      <FileSelector bind:file={modelFile} acceptType=".json" message="Select file with model"/>
   </Container>

   {#if modelFile}
   <Container name="load-button" label="&nbsp;">
      <Button onclick={readFile} text="Load" />
   </Container>
   {/if}

{:else}

   <!-- dataset is loaded - show info and cancel button -->
   <Container name="info">
      <p class="info-text">
         <span>Model file:</span> <span style="color:#606060;" title={model.pls.fileName} class="file-name">{model.pls.fileName}</span><br>
         <span style="color:#a0a0a0;">Model info:</span> {model.pls.ncomp} LVs{model.pls.center ? ', centered' : ''}{model.pls.scale ? ', scaled' : ''}
         {#if model.da.ncomp}
         <br><span style="color:#a0a0a0;">Optimal LVs:</span> <span style="color:#606060;">{model.da.ncomp}</span>
         {/if}
         <br><span style="color:#a0a0a0;">Classes:</span> {model.da.classNames.join(", ")}
         {#if model.pls.prep}
         <br><span style="color:#a0a0a0;">Preprocessing: </span>
            <PrepInfo prepmodel={model.pls.prep} />
         {/if}
         {#if model.pls.exclvars.length > 0}
         <br><span style="color:#a0a0a0;">Excluded</span> {model.pls.exclvars.length} <span style="color:#a0a0a0;">variables.</span>
         {/if}


      </p>
      <ButtonCancel onclick={reset} />
   </Container>

{/if}

<!-- error message -->
{#if status === 'error' || errMsg}
<Container name="error" status="error">
   <p>{errMsg}</p>
</Container>
{/if}


<style>

   .info-text {
      line-height: 1.5em;
   }
   .info-text > :global(span) {
      vertical-align:top;
      color: #a0a0a0;
   }

   .info-text > .file-name {
      display: inline-block;
      padding: 0;
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 25ch;
      color: #222;
   }

</style>